import { isJapaneseHoliday } from "@chillnn-core/holiday-jp";
import { useCallback, useMemo } from "react";

import { cn } from "@/lib/chillnn/cn";
import dayjs from "@/lib/dayjs";
import { type PriceRankItem } from "@/lib/gql/__generated__/graphql";

import type { ProductRoomPriceRankDailyStatusFormModel } from "../../../model/price-rank-and-daily-status/_shared/daily-stasus/type";

type Props = {
	selectedPriceRank: PriceRankItem | undefined;

	status: ProductRoomPriceRankDailyStatusFormModel["ViewModel"]["statuses"]["value"][0];

	onSetPriceRank: ({ selectedPriceRank, date }: { selectedPriceRank: PriceRankItem; date: string }) => void;
};

export const RoomPriceRankCalendarDailyStatusItem = ({ selectedPriceRank, status, onSetPriceRank }: Props) => {
	const [_year, _month, date] = status.date.value.split("-");

	// 過去の日付かどうか（dayjs）
	const isPastDate = dayjs(status.date.value).isBefore(dayjs(), "day");

	// 曜日・祝日の色判定
	const dateTextColorClass = useMemo(() => {
		if (!date) return "text-zinc-800";
		const d = new Date(String(status.date.value));
		const holiday = isJapaneseHoliday(d);
		const dayOfWeek = d.getDay(); // 0=日, 6=土
		if (dayOfWeek === 0 || holiday) return "text-not-entered";
		if (dayOfWeek === 6) return "text-edited";
		return "text-zinc-800";
	}, [date, status.date.value]);
	// 未設定の場合
	const isUnsetDate = !!status.date.value && !status.priceRankID?.value;

	const isClickingMainButton = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
		/**
		 * MouseEvent.buttons は読み取り専用のプロパティで、マウスイベントが発行されたときにマウス（またはその他の入力機器）のどのボタンが押されていたかを示す整数を返す
		 * @example
		 * 0: ボタンが押されていない、または初期化されていない
		 * 1: 主ボタン (通常は左ボタン)
		 * 2: 副ボタン (通常は右ボタン)
		 * 4: 補助ボタン (通常はマウスホイールボタンまたは中央ボタン)
		 * 8: 第四ボタン (通常は「ブラウザーで戻る」ボタン)
		 * 16 : 第五ボタン (通常は「ブラウザーで進む」ボタン)
		 *
		 * @see https://developer.mozilla.org/ja/docs/Web/API/MouseEvent/buttons
		 */
		return event.buttons === 1;
	}, []);

	const handleMouseEnter = useCallback<React.MouseEventHandler<HTMLDivElement>>(
		(event) => {
			if (
				isClickingMainButton(event) && // 主ボタン (通常は左ボタン)が押されている場合
				selectedPriceRank
			) {
				if (!isPastDate) {
					// 過去の日付でない場合
					onSetPriceRank({
						selectedPriceRank,
						date: status.date.value,
					});
				}
			}
		},
		[isClickingMainButton, isPastDate, onSetPriceRank, selectedPriceRank, status.date.value],
	);

	const handleMouseOut = useCallback<React.MouseEventHandler<HTMLDivElement>>(
		(event) => {
			if (
				isClickingMainButton(event) && // 主ボタン (通常は左ボタン)が押されている場合
				selectedPriceRank
			) {
				if (!isPastDate) {
					// 過去の日付でない場合
					onSetPriceRank({
						selectedPriceRank,
						date: status.date.value,
					});
				}
			}
		},
		[isClickingMainButton, isPastDate, onSetPriceRank, selectedPriceRank, status.date.value],
	);

	const handleClick = useCallback<React.MouseEventHandler<HTMLDivElement>>(() => {
		if (selectedPriceRank && !isPastDate) {
			onSetPriceRank({ selectedPriceRank, date: status.date.value });
		}
	}, [isPastDate, onSetPriceRank, selectedPriceRank, status.date.value]);

	return (
		<div className="relative">
			{date ? (
				<div
					onMouseEnter={handleMouseEnter}
					onMouseOut={handleMouseOut}
					onClick={handleClick}
					style={{
						backgroundColor: status.color.value ?? "#fff",
					}}
					className={
						"col-span-1 aspect-square h-auto w-full select-none rounded-md border border-zinc-300 text-center text-xs laptop:aspect-auto laptop:h-14 laptop:w-16 laptop:text-sm"
					}
				>
					<div className="flex h-full flex-col items-center justify-center gap-y-0.5 laptop:h-14">
						<p className={cn("leading-none laptop:leading-tight", dateTextColorClass)}>{date}</p>
						{isUnsetDate && <p className="hidden text-[10px] leading-tight text-zinc-800 laptop:block">※未設定</p>}
					</div>
				</div>
			) : (
				<div className="col-span-1 aspect-square h-auto w-full select-none rounded-md laptop:aspect-auto laptop:h-14 laptop:w-16" />
			)}

			{isPastDate && (
				<div className="absolute inset-0 col-span-1 select-none rounded-md bg-white/55 hover:cursor-not-allowed" />
			)}
		</div>
	);
};

RoomPriceRankCalendarDailyStatusItem.displayName = "RoomPriceRankCalendarDailyStatusItem";
